import React, { useState } from "react";
import { Link } from "react-router-dom";
import StudioOrderSelect from "../../orders/StudioOrderSelect";
import { linkMemoOrder, type MemoOrderLink as LinkView } from "../../../services/studioSalesApi";
import SalesSheet from "./SalesSheet";
import { isParcelsOff } from "./parcelModel";

const offline = "Check your connection and try again";
const msg = (res: { message?: string } | null | undefined, fallback: string) => {
  const text = res?.message || "";
  return !text || /^[A-Z_]+$/.test(text) || /exception/i.test(text) ? fallback : text;
};

/**
 * "Order: ORD-0007 · Change" on a sale or approval memo, or "Link to order". The memo belongs to at most one studio
 * order; an order may have many memos. Read-only on a cancelled memo. `shown`: the caller knows the feature is on.
 */
export const MemoOrderLink = ({ memo, shown, canEdit, onLinked }: {
  memo: any;
  shown: boolean;
  canEdit: boolean;
  onLinked: (view: LinkView) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [off, setOff] = useState(false);
  const type = memo?.type || "SALE";
  if (!shown || off || !memo?.id || (type !== "SALE" && type !== "APPROVAL")) return null;
  const linkedId: string = memo.studioOrderId || "";
  const linkedCode: string = memo.studioOrderCode || "";
  const editable = canEdit && memo.status !== "CANCELLED";
  if (!linkedId && !editable) return null;

  return (
    <div className="sp-row mo-link">
      <span className="t13 mo-link__label">Order</span>
      {linkedId ? (
        <Link className="mono grow clip mo-link__code" to={`/orders/${linkedId}`}>{linkedCode || "Open order"}</Link>
      ) : (
        <span className="t13 grow">Not linked</span>
      )}
      {editable ? (
        <button type="button" className="sp-link pc-inline" onClick={() => setOpen(true)}>{linkedId ? "Change" : "Link to order"}</button>
      ) : null}
      {open ? (
        <OrderLinkSheet memo={memo} onClose={() => setOpen(false)} onOff={() => { setOpen(false); setOff(true); }}
          onLinked={(view) => { setOpen(false); onLinked(view); }} />
      ) : null}
    </div>
  );
};

const OrderLinkSheet = ({ memo, onClose, onOff, onLinked }: {
  memo: any; onClose: () => void; onOff: () => void; onLinked: (view: LinkView) => void;
}) => {
  const [picked, setPicked] = useState<{ id: string; code: string }>({ id: memo.studioOrderId || "", code: memo.studioOrderCode || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const linked = !!memo.studioOrderId;

  const save = async (orderId: string | null) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await linkMemoOrder(memo.id, orderId);
      if (isParcelsOff(res)) return onOff();
      if (!res?.success || !res.data) return setError(msg(res, "Order link not saved"));
      onLinked(res.data);
    } catch {
      setError(offline);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SalesSheet label="Link to order" onClose={onClose}>
      <form className="pc-form" noValidate onSubmit={(e) => { e.preventDefault(); if (picked.id) void save(picked.id); }}>
        <h2>{linked ? "Change order" : "Link to order"}</h2>
        <p className="t13">Memo <span className="mono">{memo.memoCode}</span> and its parcels show on the order you pick.</p>
        <div>
          <label className="pc-label" htmlFor="studioOrderCode">Studio order</label>
          <StudioOrderSelect valueId={picked.id} valueCode={picked.code} menuZIndex={1060}
            hint="Search by order number, customer or design."
            onChange={(next) => { setPicked(next); setError(""); }} />
        </div>
        {error ? <div className="sp-warn" role="alert">{error}</div> : null}
        <div className="pc-foot sp-btns">
          {linked ? (
            <button type="button" className="sp-btn" disabled={busy} onClick={() => void save(null)}>Remove link</button>
          ) : (
            <button type="button" className="sp-btn" onClick={onClose}>Cancel</button>
          )}
          <button type="submit" className="sp-btn is-pri" disabled={busy || !picked.id || picked.id === memo.studioOrderId}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </SalesSheet>
  );
};

export default MemoOrderLink;
